import React from 'react';
import { parse as parseQuery } from 'qs';
import { useHistory, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { PortalLimitsHistory, useScreenQuery } from 'playup-web-core';

import { getLimitsHistory } from '../../../api/users';
import paginationBuilder from '../../../utils/paginationBuilder';
import { mergeApiErrors } from '../../../utils/parseApiError';
import Pagination from '../../../components/Pagination';
import appConfig from '../../../appConfig';
import PortalDatePicker from '../../../components/DatePicker/PortalDatePicker';
import { formatDate } from '../../../utils/dateFormatters';
import Loader from '../../../components/Loader';

const isUS = appConfig.regions.isUS;

interface State {
	fromDate?: Date;
	toDate?: Date;
}

const LimitsHistory = () => {
	const location = useLocation();
	const history = useHistory();
	const { isSmall } = useScreenQuery();

	const [limitsHistoryParams, setLimitsHistoryParams] = React.useState<State>({
		fromDate: undefined, // in the past
		toDate: new Date(), // most recent (today)
	});

	const { page } = parseQuery(location.search, {
		ignoreQueryPrefix: true,
	});

	const [loading, setLoading] = React.useState(false);

	const [limitsHistory, setLimitsHistory] = React.useState<Array<LimitsHistory>>();
	const [pagination, setPagination] = React.useState<Array<PaginationLink>>([]);
	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

	React.useEffect(() => {
		const fromDateString = limitsHistoryParams.fromDate
			? dayjs(limitsHistoryParams.fromDate).startOf('day').toISOString()
			: undefined;
		const toDateString = limitsHistoryParams.toDate
			? dayjs(limitsHistoryParams.toDate).endOf('day').toISOString()
			: undefined;

		setErrorMessage(null);
		setLoading(true);
		getLimitsHistory((page as string) ?? '1', fromDateString, toDateString)
			.then((response) => {
				setLimitsHistory(response?.data ?? []);
				setPagination(paginationBuilder(response?.meta.pagination));
			})
			.catch((error) => {
				if (typeof error === 'string') setErrorMessage(error);
				else if (error instanceof Error) setErrorMessage(error.message);
				else if (error.errors) setErrorMessage(mergeApiErrors(error.errors));
				else setErrorMessage('An error happened while loading your limit history, please try again.');
				setLimitsHistory([]);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [page, limitsHistoryParams.fromDate, limitsHistoryParams.toDate]);

	const resetPage = React.useCallback(() => {
		history.replace(window.location.pathname);
	}, [history]);

	React.useEffect(() => {
		return () => {
			resetPage();
		};
	}, [resetPage]);

	return (
		<>
			<DatepickerRow>
				<PortalDatePicker
					label="From:"
					value={limitsHistoryParams.fromDate ? formatDate(dayjs(limitsHistoryParams.fromDate)) : undefined}
					openToDate={limitsHistoryParams.fromDate}
					maxDate={limitsHistoryParams.toDate ?? new Date()}
					onChange={(fromDate) => {
						if (fromDate instanceof Date || fromDate === null) {
							setLimitsHistoryParams((oldParams) => ({
								...oldParams,
								fromDate: fromDate ?? undefined,
							}));

							if (page) resetPage();
						}
					}}
					showClear
				/>
				<PortalDatePicker
					label="To:"
					value={limitsHistoryParams.toDate ? formatDate(dayjs(limitsHistoryParams.toDate)) : undefined}
					openToDate={limitsHistoryParams.toDate}
					maxDate={new Date()}
					minDate={limitsHistoryParams.fromDate}
					onChange={(toDate) => {
						if (toDate instanceof Date || toDate === null) {
							setLimitsHistoryParams((oldParams) => ({
								...oldParams,
								toDate: toDate ?? undefined,
							}));

							if (page) resetPage();
						}
					}}
					showClear
				/>
			</DatepickerRow>

			{!limitsHistory && loading && (
				<LoaderContainer>
					<Loader />
				</LoaderContainer>
			)}
			{limitsHistory && (
				<>
					<PortalLimitsHistory limits={limitsHistory} isUS={isUS} errorMessage={errorMessage} />
					<Pagination links={pagination} smallScreen={isSmall} />
				</>
			)}
		</>
	);
};

export default LimitsHistory;

const DatepickerRow = styled.div`
	display: flex;
	margin: 0.5rem 0;
	height: 2.5rem;
`;

const LoaderContainer = styled.div`
	display: flex;
	margin: 2rem 0;
	justify-content: center;
`;
